import { toZonedTime, fromZonedTime, formatInTimeZone } from "date-fns-tz";
import type { ConvertTimezoneOptions } from "@multisystemsuite/timezone-engine-shared-types";
import { memoize, TimezoneCache } from "@multisystemsuite/timezone-engine-utils";

const conversionCache = new TimezoneCache();

function toDate(input: Date | string | number): Date {
  if (input instanceof Date) return input;
  return new Date(input);
}

/** Convert date to UTC */
export function toUTC(date: Date | string | number, sourceTimezone: string): Date {
  const d = toDate(date);
  return fromZonedTime(d, sourceTimezone);
}

/** Convert UTC date to target timezone representation */
export function fromUTC(date: Date | string | number, targetTimezone: string): Date {
  const d = toDate(date);
  return toZonedTime(d, targetTimezone);
}

/** Convert date between timezones */
export function convertTimezone(options: ConvertTimezoneOptions): Date {
  const { sourceTimezone, targetTimezone, date = new Date() } = options;
  const utcDate = toUTC(date, sourceTimezone);
  return fromUTC(utcDate, targetTimezone);
}

const memoizedConvert = memoize(
  (sourceTimezone: string, targetTimezone: string, timestamp: number) =>
    convertTimezone({ sourceTimezone, targetTimezone, date: new Date(timestamp) }),
  (source, target, ts) => `${source}:${target}:${ts}`,
  conversionCache,
);

/** Cached timezone conversion for high-performance scenarios */
export function convertTimezoneCached(
  sourceTimezone: string,
  targetTimezone: string,
  date: Date | string | number = new Date(),
): Date {
  return memoizedConvert(sourceTimezone, targetTimezone, toDate(date).getTime());
}

/** Detect if DST is active for a timezone at a given date */
export function detectDST(timezone: string, date: Date | string | number = new Date()): boolean {
  const d = toDate(date);
  const jan = new Date(d.getFullYear(), 0, 1);
  const jul = new Date(d.getFullYear(), 6, 1);

  const janOffset = getTimezoneOffsetMinutes(timezone, jan);
  const julOffset = getTimezoneOffsetMinutes(timezone, jul);
  const currentOffset = getTimezoneOffsetMinutes(timezone, d);

  if (janOffset === julOffset) return false;
  return currentOffset !== Math.max(janOffset, julOffset);
}

/** Get UTC offset string for a timezone (e.g. "+05:30") */
export function getUtcOffset(timezone: string, date: Date | string | number = new Date()): string {
  const d = toDate(date);
  const formatted = formatInTimeZone(d, timezone, "xxx");
  return formatted;
}

/** Get offset in minutes from UTC */
export function getTimezoneOffsetMinutes(
  timezone: string,
  date: Date | string | number = new Date(),
): number {
  const d = toDate(date);
  const utcDate = toUTC(d, timezone);
  return (utcDate.getTime() - d.getTime()) / 60_000;
}
